import React, { FC } from 'react';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import StockedPartTag from '../parts_index_page/StockedPartTag';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Typography } from 'antd';
import { LabelEntityType, PartsAddLabelButton, LabelDisplay } from '../LabelSystem';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import PartsLabelDisplay from '../LabelDisplay/PartsLabelDisplay';

const { Text } = Typography;

require('./PartRowDisplay.less');

export const getPartNumberText = (part) => (
	<span>
		{nullSafeGet('partNumber', part) ? <span>Part #{part.partNumber}</span> : null}
		{nullSafeGet('aliasPartId', part) ? (
			<span className={part.partNumber ? 'ml-4' : ''}>Alias Part #{part.aliasPartId}</span>
		) : null}
	</span>
);

const PartRowDisplay: FC<any> = ({ part, refreshParts, ...props }) => {
	const previewImage =
		part.images && part.images.length > 0
			? part.images.map((locImg) => {
					const [imgFileId, imgFileName] = locImg.split('/');
					return getProtectedImageUriForSupplier(imgFileId, imgFileName, 1200, 800);
			  })[0]
			: null;

	// Prefer hydrated partLabels; fall back to bare { id } objects from labelIds so the
	// Add Label modal seeds the already-associated ids into its selection. Without this,
	// a part that only has labelIds (not hydrated partLabels) opens the modal with an empty
	// selection, and saving would send only the newly picked ids — dropping existing labels.
	const partLabels = nullSafeGetOrElse('partLabels', part, []);
	const existingLabels = partLabels.length
		? partLabels
		: nullSafeGetOrElse('labelIds', part, []).map((id) => ({ id }));

	return (
		<div className="partRowDisplay group" style={{ justifyContent: 'space-between' }} {...props}>
			<div className="partRowDisplay">
				<div style={{ marginRight: 24 }}>
					{previewImage ? (
						<FlexibleImageAvatar
							displayName={part.name}
							showPopover={false}
							popoverText={part.name}
							squareMode={true}
							width="120px"
							height="80px"
							imageUrl={previewImage}
						/>
					) : (
						<FlexibleImageAvatar
							displayName={part.name}
							showPopover={false}
							popoverText={part.name}
							squareMode={true}
							width="120px"
							height="80px"
							imageUrl="https://s3.amazonaws.com/emptor-data-assets/images/empty-state/icons8-screw-filled-100.png"
						/>
					)}
				</div>
				<div>
					<div className="partRowDisplay__rowTitle flex items-center">
						<div className="min-w-0 flex-shrink" style={{ maxWidth: 'fit-content' }}>
							<Ellipsis
								tooltip={true}
								lines={1}
								className={'pb-0.5'}
								style={{ lineHeight: '24px' }}
							>
								{part.name ? part.name.trim() : ''}
							</Ellipsis>
						</div>
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
							<div className="ml-2 hidden shrink-0 group-hover:inline-flex">
								<PartsAddLabelButton
									entityType={LabelEntityType.PART}
									entityId={part.id}
									existingLabels={existingLabels}
									refreshTable={refreshParts}
								/>
							</div>
						</AccessPermissionChecker>
					</div>
					<div className="partRowDisplay__rowSubtitle flex flex-col">
						<Ellipsis tooltip={true} lines={1} fullWidthRecognition>
							{getPartNumberText(part)}
						</Ellipsis>
						{nullSafeGet('glCode', part) ? <div>GL Code: {nullSafeGet('glCode', part)}</div> : null}
						{part.description && (
							<Ellipsis tooltip={true} length={80}>
								{part.description}
							</Ellipsis>
						)}
						{nullSafeGet('manufacturerName', part) && (
							<div>Manufacturer: {nullSafeGet('manufacturerName', part)}</div>
						)}
						<div className="mt-2 flex flex-row flex-wrap items-center gap-x-4 gap-y-2">
							<StockedPartTag isStocked={part.isStocked} />
							{part.isActive ? null : (
								<div>
									<Text className={'mr-1'} type={'danger'}>
										Inactive
									</Text>
								</div>
							)}
							{nullSafeGetOrElse('partLabels', part, []).map((label) => (
								<LabelDisplay tag={label} key={label.id} />
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PartRowDisplay;
